
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, Plus, Info, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ContentSection from '@/components/reader-home/ContentSection';
import { Article } from '@/components/reader-home/types';

interface Tag {
  id: string;
  name: string;
  article_count: number;
  slug: string;
  description?: string;
}

interface TopicDetailsProps {
  selectedTopic: string;
  topics: Tag[];
  relatedTopics: Tag[];
  topicArticles: Article[];
  featuredJournalists: any[];
  followedTopics: string[];
  toggleFollowTopic: (topicId: string) => void;
  setSelectedTopic: (topic: string | null) => void;
}

const TopicDetails: React.FC<TopicDetailsProps> = ({
  selectedTopic,
  topics,
  relatedTopics,
  topicArticles,
  featuredJournalists,
  followedTopics,
  toggleFollowTopic,
  setSelectedTopic
}) => {
  const navigate = useNavigate();
  const currentTopic = topics.find(t => t.name === selectedTopic);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">{selectedTopic}</h2>
        {currentTopic && (
          <Button 
            variant={followedTopics.includes(currentTopic.id) ? "default" : "outline"}
            onClick={() => toggleFollowTopic(currentTopic.id)}
          >
            {followedTopics.includes(currentTopic.id) ? (
              <>Following <Heart className="ml-2 h-4 w-4 fill-primary-foreground text-primary-foreground" /></>
            ) : (
              <>Follow <Plus className="ml-2 h-4 w-4" /></>
            )}
          </Button>
        )}
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="journalists">Journalists</TabsTrigger>
          <TabsTrigger value="related">Related Topics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          {currentTopic?.description && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Info className="mr-2 h-5 w-5" />
                  About This Topic
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p>{currentTopic.description}</p>
              </CardContent>
            </Card>
          )}
          
          <ContentSection 
            title="Featured Articles" 
            articles={topicArticles}
            emptyStateMessage={`No articles found for ${selectedTopic}`}
            emptyStateAction={
              <Button 
                onClick={() => setSelectedTopic(null)}
                variant="outline"
                className="mt-4"
              >
                Explore other topics
              </Button>
            }
          />
          
          <div>
            <h3 className="text-xl font-semibold mb-4">Related Topics</h3>
            <div className="flex flex-wrap gap-2">
              {relatedTopics.map(related => (
                <Button 
                  key={related.id} 
                  variant="outline" 
                  onClick={() => setSelectedTopic(related.name)}
                >
                  {related.name}
                </Button>
              ))}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="journalists">
          <h3 className="text-xl font-semibold mb-4">Leading Journalists</h3>
          {featuredJournalists.length === 0 ? (
            <div className="py-8 text-center border border-dashed border-border rounded-lg">
              <p className="text-muted-foreground">No journalists found writing about this topic</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredJournalists.map(journalist => (
                <Card key={journalist.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={journalist.avatar_url} alt={journalist.display_name} />
                        <AvatarFallback>{journalist.display_name.substring(0, 2)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-bold">{journalist.display_name}</h4>
                        <p className="text-sm text-muted-foreground">{journalist.bio?.substring(0, 60) || "Journalist"}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-sm text-muted-foreground">{journalist.subscriber_count || 0} followers</span>
                      <Button size="sm" onClick={() => navigate(`/journalist/${journalist.id}`)}>View Profile</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="related">
          <h3 className="text-xl font-semibold mb-4">Related Topics</h3>
          <Card>
            <CardContent className="p-6">
              <div className="mb-6">
                <h4 className="font-semibold mb-2">Primary Topic</h4>
                <Badge className="text-lg py-1 px-3">{selectedTopic}</Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatedTopics.map(topic => (
                  <Button 
                    key={topic.id} 
                    variant="outline" 
                    className="justify-start h-auto py-3"
                    onClick={() => setSelectedTopic(topic.name)}
                  >
                    <div className="text-left">
                      <div className="font-medium">{topic.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {topic.article_count} articles
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
            <CardFooter className="flex justify-center border-t p-4">
              <Button variant="outline" onClick={() => navigate('/home')}>
                <Filter className="mr-2 h-4 w-4" />
                Explore Your Feed
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TopicDetails;
